import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { auth } from "../firebase";
import {
  onAuthStateChanged,
  signOut,
  signInWithCustomToken,
} from "firebase/auth";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { updateUserTheme, themeColors } from "../utils/Utility";
import { useNavigate } from "react-router-dom";
import Loader from "../utils/Loader";
import { getUserPublicKey } from "../utils/Utility";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userClaims, setUserClaims] = useState(null);
  const [userData, setUserData] = useState(null);
  const [invoiceClients, setInvoiceClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState({});
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [privateKeyInMemory, setPrivateKeyInMemory] = useState("");

  // Add states for pending navigation
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [userDataReady, setUserDataReady] = useState(false);

  const navigate = useNavigate();

  const getIsEmbedded = () => {
    try {
      // Method 1: Compare window objects
      if (window.self !== window.top) {
        return true;
      }
    } catch (error) {
      console.warn("Error detecting iframe:", error);
      return false;
    }
  };

  // Clear all user-related state
  const clearUserState = useCallback(() => {
    setUser(null);
    setUserClaims(null);
    setUserData(null);
    setInvoiceClients([]);
    setUserDataReady(false);
    setPendingNavigation(null);
    setTheme({});
    setPrivateKeyInMemory("");

    try {
      sessionStorage.removeItem("enc_key");
    } catch (error) {
      console.warn("Could not clear key from sessionStorage:", error);
    }
  }, []);

  useEffect(() => {
    try {
      const storedKey = sessionStorage.getItem("enc_key");
      if (storedKey) {
        setPrivateKeyInMemory(storedKey);
      }
    } catch (error) {
      console.warn("Could not read key from sessionStorage:", error);
    }
  }, []);

  useEffect(() => {
    let timeoutId;
    let authStateInitialized = false;
    const isEmbedded = getIsEmbedded();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Skip processing if we're in the middle of authentication
      if (isAuthenticating) {
        return;
      }

      if (!authStateInitialized) {
        authStateInitialized = true;
        clearTimeout(timeoutId);
      }

      setUser(firebaseUser);
      setUserDataReady(false);

      if (firebaseUser) {
        try {
          // Add retry logic for Safari iframe
          let retries = 3;
          let idTokenResult;

          while (retries > 0) {
            try {
              idTokenResult = await firebaseUser.getIdTokenResult();
              break;
            } catch (error) {
              retries--;
              if (retries === 0) throw error;
              console.warn(
                `Safari: Retrying getIdTokenResult, ${retries} attempts left`
              );
              await new Promise((resolve) => setTimeout(resolve, 1000));
            }
          }

          const claims = idTokenResult.claims;
          setUserClaims(claims);

          // Add retry logic for Firestore operations
          let firestoreRetries = 3;
          let userDocSnap;

          while (firestoreRetries > 0) {
            try {
              const userRef = doc(db, "users", firebaseUser.uid);
              userDocSnap = await getDoc(userRef);
              break;
            } catch (error) {
              firestoreRetries--;
              if (firestoreRetries === 0) throw error;
              console.warn(
                `Retrying Firestore fetch, ${firestoreRetries} attempts left`
              );
              // Force token refresh before retry
              await firebaseUser.getIdToken(true);
              await new Promise((resolve) => setTimeout(resolve, 1000));
            }
          }

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserData(userData);

            const theme = userData.userTheme || {};
            if (Object.keys(theme).length > 0) {
              const presets = themeColors();
              const matchingPreset = presets.find((p) => p.id === theme.id);
              if (matchingPreset) {
                updateUserTheme(matchingPreset);
                setTheme(matchingPreset);
              }
            }

            // Retry logic for invoice clients
            try {
              await fetchInvoiceClients(firebaseUser.uid);
            } catch (clientError) {
              console.warn("Failed to fetch invoice clients:", clientError);
              // Continue anyway, clients can be loaded later
            }

            setUserDataReady(true);
          } else {
            console.error("No user data found in Firestore.");
            setUserDataReady(true);
          }
        } catch (error) {
          console.error("Error during user data fetch:", error);

          // If it's a permissions error, the token might not be ready yet
          if (error.code === "permission-denied") {
            console.warn(
              "Permission denied - token may not be fully propagated yet"
            );
          }

          setUserClaims(null);
          setUserDataReady(true);
        }
      } else {
        clearUserState();
      }

      setLoading(false);
    });

    // Extended timeout for iframe/Safari
    const timeoutDuration = isEmbedded ? 15000 : 10000;
    timeoutId = setTimeout(() => {
      if (!authStateInitialized) {
        console.warn(
          "Safari: Auth state took too long, forcing loading to false"
        );
        setLoading(false);
        setUserDataReady(true);
      }
    }, timeoutDuration);

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [isAuthenticating, clearUserState]);

  // Effect to handle pending navigation when user data is ready
  useEffect(() => {
    if (userDataReady && pendingNavigation && userData && !loading) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [userDataReady, pendingNavigation, userData, navigate, loading]);

  // Helper function to check if user is admin
  const isAdmin = useCallback(() => {
    return userClaims?.admin === true || userClaims?.role === "admin";
  }, [userClaims]);

  // Fetch invoice clients from Firestore
  const fetchInvoiceClients = async (uid) => {
    try {
      const clientsRef = collection(db, "users", uid, "invoiceClients");
      const snapshot = await getDocs(clientsRef);

      const clients = [];
      snapshot.forEach((doc) => {
        clients.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      // Sort clients by name or creation date if needed
      clients.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      setInvoiceClients(clients);
    } catch (error) {
      // Don't log error for permission issues during initial load
      if (error.code !== "permission-denied") {
        console.error("Error fetching invoice clients:", error);
      }
      setInvoiceClients([]);
    }
  };

  // Function to refresh invoice clients (useful after adding/updating/deleting)
  const refreshInvoiceClients = async () => {
    if (user) {
      await fetchInvoiceClients(user.uid);
    }
  };

  // Helper function to add a new client to the state
  const addInvoiceClient = useCallback((newClient) => {
    setInvoiceClients((prev) => {
      const updated = [...prev, newClient];
      return updated.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    });
  }, []);

  // Helper function to update a client in the state
  const updateInvoiceClient = useCallback((clientId, updatedData) => {
    setInvoiceClients((prev) =>
      prev
        .map((client) =>
          client.id === clientId ? { ...client, ...updatedData } : client
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    );
  }, []);

  // Helper function to remove a client from the state
  const removeInvoiceClient = (clientId) => {
    setInvoiceClients((prev) =>
      prev.filter((client) => client.id !== clientId)
    );
  };

  // Enhanced logout function
  const logout = async () => {
    try {
      setIsAuthenticating(true);
      await signOut(auth);
      clearUserState();
    } catch (error) {
      console.error("Error signing out", error);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const getRecipients = useCallback(async () => {
    const appPublicKey = process.env.REACT_APP_PUBLIC_KEY;
    let recipients = {
      admin: appPublicKey,
    };

    if (!userData) {
      console.warn("No current user data available.");
      return recipients;
    }

    // Include the current user's public key if available
    const currentUserPublicKey = userData.publicKey || null;
    if (currentUserPublicKey) {
      recipients[userData.uid] = currentUserPublicKey;
    }

    // Add recipients from userData.addedBy array.
    if (userData.addedBy && Array.isArray(userData.addedBy)) {
      for (const contributor of userData.addedBy) {
        if (contributor.uid) {
          const pubKey = await getUserPublicKey(contributor.uid);
          if (pubKey) {
            recipients[contributor.uid] = pubKey;
          }
        }
      }
    }

    return recipients;
  }, [userData]);

  const getRecipientById = useCallback(
    async (id) => {
      const appPublicKey = process.env.REACT_APP_PUBLIC_KEY;
      let recipients = {
        admin: appPublicKey,
      };

      if (!userData) {
        console.warn("No current user data available.");
        return recipients;
      }

      // Include the current user's public key if available
      const currentUserPublicKey = userData.publicKey || null;
      if (currentUserPublicKey) {
        recipients[userData.uid] = currentUserPublicKey;
      }

      if (id !== user.uid) {
        try {
          const pubKey = await getUserPublicKey(id);
          if (pubKey) {
            recipients[id] = pubKey;
          }
        } catch (error) {
          console.error("Error fetching recipient key: ", error);
        }
      }

      return recipients;
    },
    [user, userData]
  );

  // Listen for postMessage events (from parent for custom token)
  useEffect(() => {
    const handleMessage = async (event) => {
      // Validate origin
      const allowedOrigins = [
        "https://uphub-app.com",
        "https://underground-project-c59ff.firebaseapp.com",
        "https://underground-project-c59ff.web.app",
        "http://localhost:3000",
        "http://localhost:5173",
      ];

      const isAllowed = allowedOrigins.some(
        (origin) =>
          event.origin === origin ||
          event.origin.startsWith("http://localhost:") ||
          event.origin.endsWith(".uphub-app.com") ||
          event.origin.endsWith(".firebaseapp.com") ||
          event.origin.endsWith(".web.app")
      );

      if (!isAllowed) {
        console.warn("Safari: Blocked message from:", event.origin);
        return;
      }

      const token = event.data?.token;
      const theme = event.data?.theme;
      const appId = event.data?.appId;
      const privateKey = event.data?.privateKey;

      // console.log(event.data);

      // Theme handling
      if (theme === "dark" || theme === "light") {
        const isDark = theme === "dark";
        document.documentElement.classList.toggle("dark", isDark);
        try {
          sessionStorage.setItem("theme", theme);
        } catch (error) {
          console.warn("Could not save theme to sessionStorage:", error);
        }
      }

      if (token && privateKey) {
        try {
          // console.log("Starting authentication process with new token");
          setIsAuthenticating(true);
          setLoading(true);

          // Force sign out current user first
          if (user) {
            // console.log("Signing out current user:", user.uid);
            await signOut(auth);
            clearUserState();
            // Wait longer to ensure complete logout
            await new Promise((resolve) => setTimeout(resolve, 1500));
          }

          // console.log("PrivateKey is recieved!");
          setPrivateKeyInMemory(privateKey);
          try {
            sessionStorage.setItem("enc_key", privateKey);
          } catch (error) {
            console.warn("Could not save key to sessionStorage:", error);
          }

          // Clear any cached auth state
          // console.log("Clearing cached state and signing in with custom token");

          // Sign in with new token
          await signInWithCustomToken(auth, token);

          // Determine target route
          const routeMap = {
            de06d1vh: "/receipts/invoices",
            a4g7k2mp: "/receipts/noc",
            zq18nx5d: "/receipts/acknowledgement",
            c7n4lzq3: "/records/transmittal",
            tr0w9cuj: "/records/case-brief",
            m2yb64qn: "/records/exec-admin",
            jv3d85kr: "/records/secretarial-records",
            hx9p1tue: "/records/process-server-logs",
            b1vw6t8y: "/records/legal-advice",
            f6ej03nh: "/records/sec-res",
          };

          const targetRoute = routeMap[appId] || "/pdf";
          setPendingNavigation(targetRoute);
        } catch (error) {
          console.error("❌ Error during authentication/navigation:", error);
          setLoading(false);
        } finally {
          setIsAuthenticating(false);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [user, clearUserState]);

  return (
    <UserContext.Provider
      value={{
        user,
        userData,
        logout,
        theme,
        isAdmin,
        invoiceClients,
        refreshInvoiceClients,
        addInvoiceClient,
        updateInvoiceClient,
        removeInvoiceClient,
        userDataReady,
        loading,
        isAuthenticating,
        getRecipients,
        getRecipientById,
        privateKeyInMemory,
      }}
    >
      {!getIsEmbedded() && loading && !userDataReady && (
        <Loader text={"Authenticating.. Please wait..."} />
      )}

      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
